/**
 * @file        src/components/PasswordInput.jsx
 * @description A password field with a reveal ("show password") toggle. Drop-in
 *              replacement for `<input type="password">` inside a `.field`:
 *              every prop is forwarded to the real input, so `required`,
 *              `autoComplete`, `value`/`onChange`, `disabled` and `placeholder`
 *              behave exactly as before and the field keeps all of `.field
 *              input`'s styling (background, border, focus ring).
 *
 *              Only the TYPE is owned here, flipping between 'password' and
 *              'text'. Nothing is stored, echoed or logged — the reveal is a
 *              per-field, per-mount, user-initiated act that resets the moment
 *              the screen unmounts.
 *
 *              Why a component and not a one-off on the login screen: the
 *              console has ~19 password inputs (login, forgot-password, add
 *              staff, platform admin users, gateway salts, tenant DB passwords).
 *              A toggle hand-rolled per screen is how they end up with different
 *              markup, different a11y and different icons.
 * @author      Kinetika Labs
 * @created     2026-09-16
 * @changelog
 *   - 2026-09-16 Claude — Initial. Login's password field was type-only, so an
 *                          admin who mistyped had no way to check it before
 *                          submitting and only learned from a failed sign-in.
 */
import { useId, useState } from 'react';
import Icon from './Icon';

/**
 * @param {string}  [id]         forwarded to the input so a sibling <label
 *                               htmlFor> still points at it; auto-generated when
 *                               omitted.
 * @param {string}  [className]  extra classes on the input itself (e.g. 'invalid').
 * @param {boolean} [disabled]   also greys out the toggle.
 */
export default function PasswordInput({ id, className = '', disabled, ...rest }) {
  const [shown, setShown] = useState(false);
  // useId so a caller that doesn't pass one still gets a label-able input
  // instead of a duplicate hardcoded id when two of these share a form.
  const auto = useId();
  const inputId = id || auto;
  const label = shown ? 'Hide password' : 'Show password';

  return (
    <div className="pw-wrap">
      <input
        id={inputId}
        type={shown ? 'text' : 'password'}
        className={className}
        disabled={disabled}
        {...rest}
      />
      {/* type="button" is load-bearing: the default inside a <form> is "submit",
          so revealing the password would post the login form. */}
      <button
        type="button"
        className="pw-toggle"
        onClick={() => setShown((s) => !s)}
        disabled={disabled}
        aria-label={label}
        aria-pressed={shown}
        title={label}
      >
        <Icon name={shown ? 'eyeOff' : 'eye'} size={17} />
      </button>
    </div>
  );
}
